import { isProd } from '@w/utils'

export const PREPARE_FOR_LOGS_DEFAULT_OPTIONS = {
  maxStringLength: 50,
  maxArrayLength: 10,
  maxObjectKeyCount: 80,

  sensitiveKeys: ['email', 'password', 'confirmPassword', 'confirm_password', 'token'],
  maskMode: 'normal' as 'normal' | 'extreme',
}

type PrepareForLogOptions = Partial<typeof PREPARE_FOR_LOGS_DEFAULT_OPTIONS> & {
  maskEnabled?: boolean
  removeMark?: boolean
}

/**
 * Prepares data for logging by handling errors, truncating, and masking sensitive information
 * @param obj - The input data to prepare
 * @param opts - Options for error handling, truncation, and masking
 * @returns The prepared data, with errors handled, truncated, and masked
 */
export function prepareForLog<T>(obj: T, opts?: Partial<PrepareForLogOptions>): unknown {
  try {
    const options: PrepareForLogOptions = {
      ...PREPARE_FOR_LOGS_DEFAULT_OPTIONS,
      maskEnabled: isProd,
      ...opts,
    }

    if (obj instanceof Response) return obj

    if (obj === undefined) return undefined
    if (obj === null) return null
    if (obj instanceof Date) return obj

    // Handle UFile instances by logging only size and path
    if (obj && typeof obj === 'object' && 'size' in obj && 'path' in obj && 'blob' in obj) {
      return { size: obj.size, path: obj.path }
    }

    if (typeof obj !== 'object') return obj // this is not a good solution because we may log sensitive data
    // throw new Error('Primitives in prepareForLog not supported. please return objects or null')

    if (obj instanceof Error)
      throw new Error('Error in prepareForLog not supported. please use prepareTelError instead')

    // @ts-ignore
    if (obj.__preparedForLog) {
      // @ts-ignore
      // biome-ignore lint/performance/noDelete: <explanation>
      if (options.removeMark) delete obj.__preparedForLog
      return obj
    }

    const truncatedData = truncate(obj, options)
    const masked = maskSensitiveData(truncatedData, options)
    if (options.removeMark) return masked
    return { __preparedForLog: true, ...masked }
  } catch (unexpectedError) {
    return {
      message: `Unexpected error in prepareForLog: ${unexpectedError instanceof Error ? unexpectedError.message : String(unexpectedError)}`,
      data: typeof obj === 'object' ? 'Object' : typeof obj,
      __preparedForLog: true,
    }
  }
}

/**
 * Truncates strings, arrays, and object keys based on provided options
 */
function truncate<T>(input: T, options: Partial<PrepareForLogOptions> = {}): T {
  try {
    // Extract truncation options
    const maxStringLength =
      options.maxStringLength ?? PREPARE_FOR_LOGS_DEFAULT_OPTIONS.maxStringLength
    const maxArrayLength = options.maxArrayLength ?? PREPARE_FOR_LOGS_DEFAULT_OPTIONS.maxArrayLength
    const maxObjectKeyCount =
      options.maxObjectKeyCount ?? PREPARE_FOR_LOGS_DEFAULT_OPTIONS.maxObjectKeyCount

    // Handle null or undefined
    if (input == null) {
      return input
    }

    // Handle Date objects
    if (input instanceof Date) {
      return input
    }

    // Handle UFile instances
    if (input && typeof input === 'object' && 'size' in input && 'path' in input && 'blob' in input) {
      return { size: input.size, path: input.path } as unknown as T
    }

    // Handle primitives (number, boolean)
    if (typeof input !== 'object' && typeof input !== 'string') {
      return input
    }

    // Handle strings
    if (typeof input === 'string') {
      if (input.length <= maxStringLength) {
        return input
      }
      return `${input.substring(0, maxStringLength)}...` as unknown as T
    }

    // Handle arrays
    if (Array.isArray(input)) {
      if (input.length <= maxArrayLength) {
        return input.map((item) => truncate(item, options)) as unknown as T
      }
      const truncated = input.slice(0, maxArrayLength).map((item) => truncate(item, options))
      truncated.push(`... (${input.length - maxArrayLength} more items)`)
      return truncated as unknown as T
    }

    // Handle objects
    const keys = Object.keys(input)
    const result = {} as Record<string, unknown>

    const keysToProcess = keys.slice(0, maxObjectKeyCount)

    for (const key of keysToProcess) {
      try {
        result[key] = truncate((input as Record<string, unknown>)[key], options)
      } catch (keyError) {
        result[key] =
          `[Error processing key: ${keyError instanceof Error ? keyError.message : String(keyError)}]`
      }
    }

    if (keys.length > maxObjectKeyCount) {
      result['...'] = `(${keys.length - maxObjectKeyCount} more keys)`
    }

    return result as unknown as T
  } catch (truncateError) {
    // If truncation fails, return a simplified version of the input
    const errorResult = {
      truncateError: `Error truncating data: ${truncateError instanceof Error ? truncateError.message : String(truncateError)}`,
      type: typeof input,
      isArray: Array.isArray(input),
    }
    return errorResult as unknown as T
  }
}

interface MaskOptions {
  sensitiveKeys?: string[]
  maskMode?: 'normal' | 'extreme'
  maskEnabled?: boolean
}

function maskSensitiveData<T>(input: T, options: MaskOptions): T {
  const { sensitiveKeys = [], maskMode = 'normal', maskEnabled = true } = options
  const mode = maskMode
  const enabled = maskEnabled

  const isSensitiveKey = (key: string) => sensitiveKeys.includes(key) || mode === 'extreme'

  const maskValue = (value: unknown, isSensitive: boolean): unknown => {
    if (value === null || value === undefined) return value

    if (typeof value === 'string' && value.trim() === '') return value

    if (isSensitive) return enabled ? '***' : `***${String(value)}`

    return value
  }

  if (input === null || input === undefined) return input
  if (input instanceof Date) return input

  if (typeof input !== 'object') {
    // In extreme mode, all primitive values bes masked
    return mode === 'extreme' ? (maskValue(input, true) as a) : input
  }

  if (Array.isArray(input)) {
    return input.map((item) => maskSensitiveData(item, options)) as unknown as T
  }

  const result = {} as Record<string, unknown>

  for (const [key, value] of Object.entries(input)) {
    if (key === 'tel') continue

    const isKeySensitive = isSensitiveKey(key)

    if (typeof value !== 'object' || value === null) {
      // Handle primitive values - in extreme mode, mask all values
      result[key] = mode === 'extreme' ? maskValue(value, true) : maskValue(value, isKeySensitive)
    } else if (Array.isArray(value)) {
      result[key] = maskSensitiveData(value, options)
    } else {
      // Handle objects
      result[key] = maskSensitiveData(value, options)
    }
  }

  return result as unknown as T
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
type a = any
